/**
 * The path prefix the SPA is deployed under. The router strips it from every
 * in-app path, and nginx only serves the SPA fallback below it, so any path
 * that leaves the router (a raw `<a href>`, `window.location`) must carry it
 * explicitly or the browser will request a URL that 404s.
 */
export const ROUTER_BASENAME = '/bank/lan/v4';

export function addUidToPath(path: string, uid?: string) {
  return path.replace(':uid', uid ?? ':uid');
}

/**
 * Decides whether a known uid must be forwarded as a `?uid=` query param when
 * navigating to `pathname`.
 *
 * A uid-less route (e.g. the intro page `/`) has no `:uid` path param, so
 * `addUidToPath` leaves it unchanged and the uid would be dropped. Dropping it on
 * a PREVIOUS back onto the intro flips `hasUid` false, so the next NEXT re-runs
 * /init and creates a duplicate application instead of resuming. Forward the uid
 * as a query param in that case — unless the route already carries it in its path
 * (a `:uid` param was substituted) or the caller already set a `uid` search
 * param.
 */
export function shouldForwardUidAsSearchParam({
  route,
  pathname,
  uid,
  existingUidSearchParam,
}: {
  route: string;
  pathname: string;
  uid?: string;
  existingUidSearchParam?: string;
}): boolean {
  const carriesUidInPath = pathname !== route;
  return Boolean(uid) && !carriesUidInPath && !existingUidSearchParam;
}

/**
 * Turns a router-relative path into one that is safe to put in a raw anchor
 * href or `window.location`. Use `Link`/`useNavigate` instead whenever the
 * navigation can stay inside the router.
 */
export function toAbsoluteAppPath(path: string) {
  return `${ROUTER_BASENAME}${path}`;
}
